import React, { useContext, useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import connectRoutes from 'shared/hoc/withRoutes';
import { connectSideNav } from 'shared/hoc/withSideNav';
import { macViewCheck, tenantCreateCheck, tenantViewCheck } from 'shared/api/tenants';
import { satelliteCheck } from 'features/tenant/api/satellite';
import { networkingViewCheck } from 'shared/api/networking';
import { FilterContext, prepareFilters } from 'shared/hoc/withFilters';
import { processRoutes } from 'shared/utils';
import { MODULES } from 'shared/constants';
import * as routes from 'shared/routes/tenant';
import ReceiverSearch from './ReceiverSearch';
import TenantNav from './TenantNav';
import TenantSearch from './TenantSearch';
import TenantView from './TenantView';
import TenantInternetSubscription from './TenantInternetSubscription';
import TenantDeviceView from './TenantDeviceView';
import TenantReceivers from './TenantReceivers';
import TenantCreator from './TenantCreator';
import TenantMover from './move/TenantMover';
import DeviceSearch from './DeviceSearch';
import DeviceHelp from './DeviceHelp';
import TenantNetworkingLinks from './TenantNetworkingLinks';
import TenantLocationHistory from './TenantLocationHistory';
import TenantTickets from './TenantTickets';

const withSideNav = connectSideNav(MODULES.tenant);

const withFilters = prepareFilters(MODULES.tenant);

const withRoutes = connectRoutes([
  {
    RouteComponent: TenantSearch,
    text: 'Search Tenants',
    path: routes.viewPath,
    routeToTest: tenantViewCheck,
    key: 'search_tenants',
  },
  {
    RouteComponent: TenantCreator,
    text: 'Create Tenants',
    path: routes.createPath,
    routeToTest: tenantCreateCheck,
  },
  {
    RouteComponent: TenantMover,
    text: 'Move Tenants',
    path: routes.movePath,
    routeToTest: tenantCreateCheck,
  },
  {
    RouteComponent: DeviceSearch,
    text: 'Search Devices',
    path: routes.deviceSearchPath,
    routeToTest: macViewCheck,
    key: 'search_devices',
  },
  {
    RouteComponent: ReceiverSearch,
    text: 'Search Receivers',
    path: routes.satelliteSearchPath,
    routeToTest: satelliteCheck,
    key: 'search_receivers',
  },
  {
    RouteComponent: DeviceHelp,
    text: 'Device Help',
    path: routes.deviceHelpPath,
  },
  {
    // Everything below this is passed to the tenant router.
    // These are filtered out of the side nav because there is no 'path' key.
    // The paths are created dynamically via the getPath function.
    RouteComponent: TenantView,
    text: 'View',
    getPath: routes.getTenantPath,
    exact: true,
  },
  {
    RouteComponent: TenantDeviceView,
    text: 'Devices',
    getPath: routes.getTenantDevicesPath,
    routeToTest: macViewCheck,
  },
  {
    RouteComponent: TenantInternetSubscription,
    text: 'Internet Subscription',
    getPath: routes.getSubscriptionPath,
    renderWhen: (_, building) => building && building.auth_method.includes('802.1x'),
  },
  {
    RouteComponent: TenantReceivers,
    text: 'Receivers',
    getPath: routes.getTenantReceiverPath,
    routeToTest: satelliteCheck,
    exact: true,
  },
  {
    RouteComponent: TenantLocationHistory,
    text: 'History',
    getPath: routes.getTenantHistoryPath,
    exact: true,
  },
  {
    RouteComponent: TenantTickets,
    text: 'Tickets',
    getPath: routes.getTenantTicketsPath,
    exact: true,
    key: 'tenant_tickets',
  },
  {
    RouteComponent: TenantNetworkingLinks,
    text: 'Networking',
    getPath: routes.getTenantLinksPath,
    renderWhen: (_, building) => building && building.unit,
    routeToTest: networkingViewCheck,
    exact: true,
  },
]);

/**
 * @param {Object} props
 * @param {Array<Object>} props.enabledRoutes - Array of enabled route objects
 */
function Tenant(props) {
  const { enabledRoutes } = props;
  const {
    savedFilters,
    getFavoriteFilters,
  } = useContext(FilterContext);

  useEffect(() => {
    if (!savedFilters) {
      getFavoriteFilters();
    }
  }, [savedFilters, getFavoriteFilters]);

  // Routes that depend on tenant id.
  const initialTenantNavRoutes = [];
  const initialStaticRoutes = [];

  const { staticRoutes, navRoutes } = processRoutes(
    enabledRoutes,
    initialStaticRoutes,
    initialTenantNavRoutes,
    savedFilters,
  );

  return (
    <Switch>
      {staticRoutes.map((route) => (
        <Route
          render={() => <route.RouteComponent filterKey={route.key || ''} savedFilters={route.filters || ''} path={route.path} />}
          exact
          key={route.path}
          path={route.path}
        />
      ))}
      <Route
        path={`${routes.viewPath}/:routeId`}
        render={({ location }) => <TenantNav enabledRoutes={navRoutes} location={location} />}
      />
    </Switch>
  );
}

export default {
  RouteComponent: withRoutes(withFilters(withSideNav(Tenant))),
  module: 'tenant',
  name: 'Tenants',
  path: routes.viewPath,
  rootPath: routes.rootPath,
  routeToTest: tenantViewCheck,
};
